import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';import {
    Box,
    Slide,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    makeStyles
} from '@mui/styles';
import {
    TypographyVariants,
    TypographyWeights
} from '../../../figmaTheme';
import {
    FormContext
} from '../../../contexts/form';
import CardContainer from './cardContainer';
import Card from './card';
import ProgressTracker from './progressTracker';

const useStyles = makeStyles(() => ({
    scrollableContainer: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        height: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    infoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%'
    },
    header: {
        fontWeight: TypographyWeights.MEDIUM,
        marginBottom: '8px',
        alignSelf: 'flex-start'
    },
    pillHeader: {
        padding: '8px 12px',
        borderRadius: '20px',
        width: 'fit-content'
    },
    fadeIn: {
        opacity: 0,
        animation: '$fadeIn 0.5s forwards 0s'
    },
    fadeOut: {
        animation: '$fadeOut 0.5s forwards 0s'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: '0'
        },
        '100%': {
            opacity: '1'
        },
    },
    '@keyframes fadeOut': {
        '0%': {
            opacity: '1'
        },
        '100%': {
            opacity: '0'
        },
    }
}));

type InfoProps = {
    position: number,
    sectionKey: string,
    pillHeader?: string,
    title?: string,
    titleColor?: string,
    subtitle?: string,
    subtitleColor?: string,
    graphic?: JSX.Element,
    // cardContainerProps?: CardContainerProps,
    /**
     * TODO: this does not match CardContainerProps (tried to export to use for type safety)
     */
    cardContainerProps?: any,
    hideProgress?: boolean,
    hideCtaButton?: boolean,
    hasBottomPadding?: boolean,
    hasGradient?: boolean,
    hidePillHeader?: boolean,
    isPillHeader?: boolean,
    progressLength?: number,
    preventIsLoading?: boolean,
    fadeIn?: boolean,
    fadeOut?: boolean,
    overridePosition?: boolean
}

const InfoScreen = ({
    position,
    sectionKey,
    pillHeader,
    title,
    titleColor,
    subtitle,
    subtitleColor,
    graphic,
    cardContainerProps,
    hideProgress = false,
    hasBottomPadding = true,
    hasGradient = false,
    hideCtaButton = false,
    hidePillHeader = false,
    isPillHeader = true,
    progressLength,
    preventIsLoading = false,
    fadeIn,
    fadeOut,
    overridePosition = false
}: InfoProps) => {
    const diagnosticFormContext = useContext(FormContext);
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const section = useMemo(() => {
        return diagnosticFormContext?.sections.find(s => s.key === sectionKey);
    }, [diagnosticFormContext?.sections, sectionKey]);
    const [isLoading, setIsLoading] = useState(false);

    const goNext = () => {
        if (!preventIsLoading) setIsLoading(true);
        cardContainerProps?.goNext ? cardContainerProps.goNext(cardContainerProps?.forwardJump || 1) : diagnosticFormContext.goNext(cardContainerProps?.forwardJump || 1);
    };

    const goBack = () => {
        setIsLoading(true);
        cardContainerProps?.goBack ? cardContainerProps.goBack(cardContainerProps?.backJump || 1) : diagnosticFormContext.goBack(cardContainerProps?.backJump || 1);
    };

    useEffect(() => {
        setIsLoading(false);
    }, [position]);

    if (isLoading) return <></>;

    return (
        <CardContainer
            sectionKey={sectionKey}
            goNext={goNext}
            goBack={cardContainerProps?.hideBackButton ? null : (overridePosition || position > 0) && goBack}
            headerLogo={cardContainerProps?.headerLogo}
            headerTextColor={cardContainerProps?.headerTextColor}
            buttonText={cardContainerProps?.buttonText || 'Continue'}
            backgroundColor={cardContainerProps?.backgroundColor}
            ctaBackgroundColor={cardContainerProps?.ctaBackgroundColor}
            ctaButtonColor={cardContainerProps?.ctaButtonColor}
            ctaButtonTextColor={cardContainerProps?.ctaButtonTextColor}
            ctaButtonIcon={cardContainerProps?.ctaButtonIcon}
            ctaButtonStyle={cardContainerProps?.ctaButtonStyle}
            ctaButtonTextStyle={cardContainerProps?.ctaButtonTextStyle}
            ctaText={cardContainerProps?.ctaText}
            ctaTextColor={cardContainerProps?.ctaTextColor}
            hideArrowButton
            showCTAButton={!hideCtaButton}
            showSecondaryCTA={cardContainerProps?.showSecondaryCTA}
            ctaOnClick={cardContainerProps?.ctaOnClick}
            hasGradient={hasGradient}
            isDisabled={cardContainerProps?.isDisabled}
            disabledButtonColor={cardContainerProps?.disabledButtonColor}
            buttonDelay={cardContainerProps?.buttonDelay}
            isButtonLoading={cardContainerProps?.isButtonLoading}
            isSecondaryCTAButtonLoading={cardContainerProps?.isSecondaryCTAButtonLoading}
            hideHeader={cardContainerProps?.hideHeader}
        >
            <Card backgroundColor={cardContainerProps?.backgroundColor} shiftUp={cardContainerProps?.hideHeader} ignoreTranslate={cardContainerProps?.hideHeader}>
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={diagnosticFormContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box
                            className={classes.content}
                        >
                            {!hidePillHeader && (
                                <>
                                    {isPillHeader ? (
                                        <Box
                                            className={classes.pillHeader}
                                            style={{
                                                marginTop: cardContainerProps?.hideHeader ? '24px' : '0px',
                                                backgroundColor: cardContainerProps?.titleBackgroundColor || section?.colors?.titleBackground,
                                                border: `1px solid ${cardContainerProps?.titleBorderColor || section?.colors?.titleBorder}`
                                            }}
                                        >
                                            <Typography
                                                variant={isMobile ? TypographyVariants.H6 : TypographyVariants.H4}
                                                style={{
                                                    fontWeight: TypographyWeights.SEMI_BOLD,
                                                    color: cardContainerProps?.titleTextColor || section?.colors?.titleText
                                                }}
                                            >
                                                {pillHeader || section?.title}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            className={classes.header}
                                            style={{
                                                color: section?.colors.main
                                            }}
                                        >
                                            {section.title}
                                        </Typography>
                                    )}
                                </>
                            )}
                            {!hideProgress && (
                                <ProgressTracker
                                    currentPosition={position - 1}
                                    sectionKey={sectionKey}
                                    length={progressLength !== undefined ? progressLength : section?.length - 1}
                                />
                            )}
                            <Box className={classes.infoContainer}>
                                {title && (
                                    <Typography
                                        variant={isMobile ? TypographyVariants.H3 : TypographyVariants.H2}
                                        style={{
                                            fontWeight: TypographyWeights.BOLD,
                                            color: titleColor || section?.colors?.main,
                                            marginTop: hideProgress && hidePillHeader ? '0px' : isMobile ? '24px' : '32px',
                                            textAlign: 'center'
                                        }}
                                        className={`${fadeIn && !fadeOut ? classes.fadeIn : ''} ${fadeOut ? classes.fadeOut : ''}`}
                                        dangerouslySetInnerHTML={{
                                            __html: title
                                        }}
                                    />
                                )}
                                {subtitle && (
                                    <Typography
                                        variant={isMobile ? TypographyVariants.H5 : TypographyVariants.H4}
                                        style={{
                                            color: subtitleColor || section?.colors?.main,
                                            marginTop: isMobile ? '12px' : '24px',
                                            textAlign: 'center'
                                        }}
                                        className={`${fadeIn && !fadeOut ? classes.fadeIn : ''} ${fadeOut ? classes.fadeOut : ''}`}
                                        dangerouslySetInnerHTML={{
                                            __html: subtitle
                                        }}
                                    />
                                )}
                                {graphic}
                                {hasBottomPadding && (
                                    <Box
                                        style={{
                                            height: '300px'
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default InfoScreen;
